<script>
import OrderBlock from "@/views/trips/edit/blocks/OrderBlock.vue";
import TravelOrderBlock from "@/views/trips/edit/blocks/TravelOrderBlock.vue";
import RouteBlock from "@/views/trips/edit/blocks/RouteBlock.vue";
import AddOrderDialog from "@/views/trips/edit/dialogs/AddOrderDialog.vue";
import { OrdersMixin } from "@/views/orders/orders.mixin";
import AddReloadStopDialog from "@/views/trips/edit/dialogs/AddReloadStopDialog.vue";
import BaseView from "@/views/BaseView.vue";
import { TravelOrderStatus } from "@/data/trip";
import { mapActions } from "pinia";
import { useSnackbarStore } from "@/store/snackbar.store";
import RouteStopBlock from "@/views/trips/edit/blocks/RouteStopBlock.vue";
import TripStopBlock from "@/views/trips/edit/blocks/TripStopBlock.vue";
import TripInfo from "@/views/trips/edit/blocks/TripInfo.vue";
import TripManager from "@/views/trips/edit/trip-manager";
import { TripGrid } from "@/views/trips/edit/trip-grid";
import { useError } from "@/composables/useError.js";
import { TRUCK_ID } from "@/data/query.ts";
import TooltipButton from "@/components/controls/TooltipButton.vue";
import { useDisableRules } from "@/composables/reactive/useDisableRules.ts";
import ReorderTripStopsDialog from "@/views/trips/edit/dialogs/ReorderTripStopsDialog.vue";

const { showAPIError } = useError();
const { createTripDispatchDisableRules } = useDisableRules();

export default {
  name: "TripEdit",
  components: {
    TripStopBlock,
    TooltipButton,
    TravelOrderBlock,
    TripInfo,
    RouteStopBlock,
    AddReloadStopDialog,
    AddOrderDialog,
    RouteBlock: RouteBlock,
    OrderBlock,
    ReorderTripStopsDialog,
  },
  mixins: [BaseView, OrdersMixin],
  data() {
    return {
      orders: [],
      deliveryNum: 0,
      pickupNum: 0,

      addOrderDialog: false,
      reorderOrderStopsDialog: false,

      addReloadStopDialog: false,
      reloadStopPosition: 1,

      tripManager: null,
      tripGrid: null,
      tripFromAPI: null,
      loading: false,
      saveLoading: false,
      isReorderStopDialog: false,
      newAddedOrder: null,
    };
  },
  computed: {
    orderFreights() {
      return this.trip.orders.flatMap((stop) => stop.freights);
    },
    cancelEnabled() {
      return this.$route.params.id && this.$route.params.id !== "new";
    },
    gridStyle() {
      return this.tripGrid.gridStyle;
    },
    stopsContainerStyle() {
      return this.tripGrid.stopsContainerStyle;
    },
    travelOrdersByOrder() {
      return this.tripManager.travelOrdersByOrder;
    },
    orderStops() {
      return this.trip.orders.flatMap((order) => order.tripStops);
    },
    routes() {
      return this.trip?.routes || [];
    },
    saveAndDispatchRules() {
      return createTripDispatchDisableRules(this.tripManager.routes, true);
    },
    trip() {
      return this.tripManager.trip;
    },
    sortedTripStops() {
      return [...this.trip.tripStops].sort((a, b) => a.position - b.position);
    },
  },
  async created() {
    this.loading = true;
    this.tripManager = new TripManager(this.$api, this.$route);
    this.tripGrid = new TripGrid(this.tripManager);
    await this.tripManager.load();

    if (this.tripManager.shouldShowReorderStopsDialog) {
      this.showReorderStopsDialog();
    }

    this.tripFromAPI = { ...this.tripManager.trip };
    this.loading = false;
  },
  methods: {
    ...mapActions(useSnackbarStore, ["showSnackbarSuccess", "showSnackbarError"]),
    addOrder(order) {
      this.newAddedOrder = order;
      this.addOrderDialog = false;
      this.tripManager.addOrderToTrip(order);
      this.showReorderStopsDialog();
    },
    removeOrder(order) {
      this.tripManager.removeOrder(order);
    },
    showReloadStopDialog(index) {
      this.addReloadStopDialog = true;
      this.reloadStopPosition = index + 2;
    },
    addReloadStop(reloadStop) {
      this.tripManager.addReloadStop({
        ...reloadStop,
        position: this.reloadStopPosition,
      });
      this.addReloadStopDialog = false;
    },
    async save() {
      const id = this.$route.params.id !== "new" ? Number(this.$route.params.id) : undefined;

      const data = this.tripManager.prepareTripForAPI(this.trip, id);

      this.saveLoading = true;
      const resp = await this.$api.trips.upsertTrip(data);

      if (resp.success) {
        this.toView(resp.data);
        this.showSnackbarSuccess(this.$t("general.saved"));
      } else {
        showAPIError(resp, this.$t("general.save-error"));
      }

      this.saveLoading = false;
    },
    setTruck(route, truck) {
      if (truck) {
        route.truck = truck;
        route.truckId = truck.id;
      } else {
        route.truck = null;
        route.truckId = null;
      }
    },
    toView(updatedTrip = null) {
      if (!updatedTrip) {
        this.$router.push({
          name: "trip-view",
          params: { id: this.$route.params.id },
        });
        return;
      }

      const truckIds = updatedTrip.routes.flatMap((route) => route.truckId);

      this.$router.replace({
        name: "trip-view",
        params: { id: updatedTrip.id || this.$route.params.id },
        query: { [TRUCK_ID]: truckIds },
      });
    },
    showAddOrderDialog() {
      this.addOrderDialog = true;
    },
    addEmptyRoute() {
      const copyFreights = this.trip.routes.every((r) =>
        r.travelOrders.every((to) =>
          [TravelOrderStatus.cancelled, TravelOrderStatus.dryRun, TravelOrderStatus.waitingForDryRun].includes(
            to.status,
          ),
        ),
      );
      this.tripManager.addRoute(null, copyFreights);
    },
    changeRoute(route, stop, freights) {
      this.tripManager.updateRouteStop(stop);

      this.$nextTick(() => {
        this.tripManager.updateFreights(route, freights, stop);
      });

      this.$nextTick(() => {
        this.tripManager.addReloadFreights(stop);
      });

      this.$nextTick(() => {
        this.tripManager.updateTravelOrderTempIds();
      });
    },
    onTravelOrderUpdate(route, travelOrder) {
      this.tripManager.updateRouteStatus(route);

      this.$nextTick(() => {
        this.tripManager.editTravelOrder(travelOrder);
      });

      this.$nextTick(() => {
        this.tripManager.editOrderByTravelOrder(travelOrder);
      });
    },
    showReorderStopsDialog() {
      this.isReorderStopDialog = true;
    },
    reorderTripStop(tripStops) {
      this.tripManager.reorderTripStops(tripStops);
    },
  },
};
</script>

<template>
  <v-container :fluid="true" class="pt-8 pb-10 pl-6 pr-6">
    <template v-if="!loading">
      <div class="pb-15">
        <div :style="gridStyle" class="stops-grid">
          <trip-info
            :orders="tripManager.trip.orders"
            @add:order="showAddOrderDialog"
            @add:route="addEmptyRoute"
            @edit:stops-order="showReorderStopsDialog"
          ></trip-info>
          <order-block
            v-for="(order, index) in trip.orders"
            :key="order.id"
            :can-remove="trip.orders.length > 1"
            :index="index"
            :order="order"
            class="mb-4"
            @remove="removeOrder"
          />
          <div
            :style="stopsContainerStyle"
            class="trips-block bg-white pa-4 pt-0 rounded-lg position-relative rounded-tl-0 rounded-tr-0 rounded-bl-0 rounded-br-0"
          >
            <div class="trip-block__header d-flex align-center justify-space-between my-2">
              <span class="text-grey-darken-2 font-weight-medium">Trip stops</span>
            </div>
            <template v-for="(stop, i) in sortedTripStops" :key="stop.id">
              <trip-stop-block
                :order-freights="orderFreights"
                :orders="trip.orders"
                :routes="trip.routes"
                :stop="stop"
              />

              <div v-if="i < sortedTripStops.length - 1" class="plus-block text-center">
                <v-btn
                  class="text-primary plus-btn"
                  icon="mdi-plus"
                  size="small"
                  variant="flat"
                  @click="showReloadStopDialog(i)"
                />
              </div>
            </template>
          </div>
          <template v-for="(route, i) in trip.routes" :key="i">
            <route-block
              v-model="trip.routes[i]"
              :can-remove="trip.routes.length > 1"
              :orders="trip.orders"
              :trip-stops="trip.tripStops"
              class="mb-4"
              @attach="(truck) => setTruck(trip.routes[i], truck)"
              @remove-truck="setTruck(trip.routes[i], null)"
              @remove-route="trip.routes.splice(i, 1)"
            />

            <template v-for="(travelOrder, index) in route.travelOrders" :key="travelOrder.tempId">
              <travel-order-block
                v-model="route.travelOrders[index]"
                :route="route"
                :route-stops="route.routeStops"
                :routes="trip.routes"
                :travel-orders-by-order="travelOrdersByOrder"
                :trip-from-api="tripFromAPI"
                :truck="route.truck"
                class="mb-4"
                @update:model-value="(to) => onTravelOrderUpdate(route, to)"
              />
            </template>

            <div
              v-if="route.routeStops.length > 0"
              :style="stopsContainerStyle"
              class="trip-block bg-white pa-4 rounded-lg position-relative rounded-tl-0 rounded-tr-0 rounded-bl-0 rounded-br-0"
              data-qa="route-stops"
            >
              <div class="trip-block__header d-flex align-center justify-space-between mb-3">
                <span class="text-grey-darken-2 font-weight-medium">Route stops</span>
              </div>
              <template v-for="(routeStop, index) in route.routeStops" :key="index">
                <route-stop-block
                  :allow-edit-usage="trip.routes.length > 1"
                  :order-freights="orderFreights"
                  :route="route"
                  :routes="trip.routes"
                  :stop="routeStop"
                  :trip-from-api="tripFromAPI"
                  :trip-manager="tripManager"
                  :trip-stops="trip.tripStops"
                  @changed:route="(stop, freights) => changeRoute(route, stop, freights)"
                />

                <div v-if="index < trip.tripStops.length - 1"></div>
              </template>
            </div>
          </template>
        </div>

        <div class="d-flex justify-end pa-5 bg-white user-info__buttons">
          <v-btn
            v-if="cancelEnabled"
            id="cancel-btn"
            class="pr-6 pl-6 font-weight-600 text-uppercase"
            color="primary"
            height="36"
            variant="text"
            @click="() => toView()"
          >
            Cancel
          </v-btn>

          <tooltip-button :rules="saveAndDispatchRules">
            <template #default="{ disabled }">
              <v-btn
                id="save-btn"
                :disabled="disabled"
                :loading="saveLoading"
                class="pr-6 pl-6 font-weight-600 text-uppercase"
                color="primary"
                height="36"
                variant="flat"
                @click="() => save()"
              >
                <template v-if="disabled">Save</template>
                <template v-else>Save and dispatch</template>
              </v-btn>
            </template>
          </tooltip-button>
        </div>
      </div>

      <add-order-dialog
        v-if="addOrderDialog"
        v-model="addOrderDialog"
        :orders="trip.orders"
        :trip-manager="tripManager"
        :trip-stops="trip.tripStops"
        @change="addOrder"
      />

      <add-reload-stop-dialog
        v-if="addReloadStopDialog"
        v-model="addReloadStopDialog"
        :position="reloadStopPosition"
        :trip-stops="trip.tripStops"
        @add="addReloadStop"
      />

      <reorder-trip-stops-dialog
        v-if="isReorderStopDialog"
        v-model="isReorderStopDialog"
        :new-order="newAddedOrder"
        :trip="trip"
        :trip-manager="tripManager"
        @reorder="reorderTripStop"
      />
    </template>
  </v-container>
</template>

<style lang="scss" scoped>
.stops-grid {
  display: grid;
  grid-auto-flow: column;
  column-gap: 24px;
  overflow-x: auto;
  width: 100%;
}

.trip-stops__block-inner {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.plus-block {
  line-height: 1;
}

.reload-stop-list {
  display: flex;
  flex-direction: column;
}
</style>
